import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Check, Gift, Zap, Mic, Headphones, BookOpen, Timer, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DailyTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const taskIcons = {
    quiz: <BookOpen className="w-6 h-6" />,
    speaking: <Mic className="w-6 h-6" />,
    listening: <Headphones className="w-6 h-6" />,
    flashcard: <Zap className="w-6 h-6" />,
    timed: <Timer className="w-6 h-6" />,
    roleplay: <MessageSquare className="w-6 h-6" />
  };

  const taskColors = {
    quiz: 'from-blue-400 to-blue-500',
    speaking: 'from-pink-400 to-rose-500',
    listening: 'from-emerald-400 to-teal-500',
    flashcard: 'from-amber-400 to-orange-500',
    timed: 'from-purple-400 to-violet-500',
    roleplay: 'from-cyan-400 to-sky-500'
  };

  const taskRoutes = {
    quiz: '/english-training',
    speaking: '/skill-test/level_1/speaking',
    listening: '/skill-test/level_1/listening',
    flashcard: '/cabin-crew/flashcards',
    timed: '/cabin-crew/timed-challenge',
    roleplay: '/cabin-crew/roleplay'
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gamification/daily-tasks`, { withCredentials: true });
      setTasks(response.data.tasks || []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const completedCount = (tasks || []).filter(t => t.completed).length;
  const totalMiles = (tasks || []).reduce((sum, t) => t.completed ? sum + t.miles : sum, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 pb-8 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Geri</span>
          </Link>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                <Target className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Günlük Görevler</h1>
                <p className="text-green-100 text-sm">Bugün {new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-white">{completedCount}/3</p>
              <p className="text-green-200 text-xs">Tamamlandı</p>
            </div>
          </div>
          
          {/* Progress */}
          <div className="mt-4 bg-white/20 rounded-full h-3">
            <div 
              className="bg-white h-3 rounded-full transition-all"
              style={{ width: `${(completedCount / 3) * 100}%` }}
            />
          </div>
          
          {completedCount === 3 && (
            <div className="mt-3 text-center">
              <span className="bg-white/20 text-white text-sm px-4 py-1 rounded-full">
                🎉 Tüm görevler tamamlandı! +{totalMiles} Mil kazandın!
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 -mt-4">
        {/* Tasks */}
        <div className="space-y-4">
          {tasks.map((task) => (
            task.completed ? (
              <div 
                key={task.id}
                className="rounded-xl shadow-lg p-4 bg-green-50 ring-2 ring-green-300"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br ${taskColors[task.type]} text-white`}>
                    {taskIcons[task.type]}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-green-700 line-through">{task.title}</h3>
                    <p className="text-sm text-gray-500">{task.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Gift className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-semibold text-amber-600">+{task.miles} Mil</span>
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={task.id}
                to={taskRoutes[task.type]}
                className="block rounded-xl shadow-lg p-4 bg-white hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br ${taskColors[task.type]} text-white`}>
                    {taskIcons[task.type]}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800">{task.title}</h3>
                    <p className="text-sm text-gray-500">{task.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Gift className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-semibold text-amber-600">+{task.miles} Mil</span>
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-lg text-white font-medium bg-gradient-to-r ${taskColors[task.type]}`}>
                    Başla
                  </div>
                </div>
              </Link>
            )
          ))}
        </div>

        {/* Info */}
        <div className="rounded-xl shadow-lg mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50">
          <h3 className="font-bold text-gray-800 mb-2">💡 İpucu</h3>
          <p className="text-sm text-gray-600">
            Her gün 3 yeni görev belirlenir. Tüm görevleri tamamlayarak ekstra mil kazan!
            Görevler gece yarısı sıfırlanır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DailyTasks;
